import type { OperationVariables } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useListAnalytics } from './useListAnalytics';
import { useListPersistence } from './useListPersistence';
import type {
  ListQueryParams,
  PageInfo,
  Paginated,
  SortState,
  UseListPaginationOptions,
  UseListPaginationResult,
} from './types';

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_SEARCH_DEBOUNCE_MS = 250;

/**
 * Turns a paginated response + current page/pageSize into the affordances the pager needs
 * ("Viewing X–Y of N", prev/next enablement). Falls back to `hasMore` for "next" when `count`
 * is absent; surfaces `countExceeded` for capped counts. "Next" waits for a load to finish, the
 * same gate `nextPage` applies.
 */
const derivePageInfo = <T>(
  result: Paginated<T> | undefined,
  page: number,
  pageSize: number,
  loading: boolean
): PageInfo => {
  const count = result?.count ?? null;
  const itemsLength = result?.items.length ?? 0;
  return {
    from: itemsLength === 0 ? 0 : page * pageSize + 1,
    to: page * pageSize + itemsLength,
    count,
    countExceeded: Boolean(result?.limitCountExceeded),
    hasPrev: page > 0,
    hasNext: !loading && Boolean(result?.hasMore),
  };
};

type CursorHistory = {
  firstPageVariablesKey: string;
  page: number;
  /** `cursorForPage[i]` is the cursor to send for page `i`; page 0 sends `null`. */
  cursorForPage: (string | null)[];
};

const firstPageHistory = (firstPageVariablesKey: string): CursorHistory => ({
  firstPageVariablesKey,
  page: 0,
  cursorForPage: [null],
});

/**
 * State + data for a **sequentially**-paginated list: pagination, sort, debounced search, an
 * arbitrary filter object, and the fetched page. Data fetching is owned by Apollo's `useQuery`
 * (loading/error/cancellation/latest-wins); the hook maps state to variables and the response to
 * a page. Presentation-agnostic.
 *
 * Pagination echoes the server's opaque cursor and never synthesizes one, so only next/prev are
 * reachable. Cursors belong to one query, identified by the variables `toVariables` builds for
 * page 0: any change to them — sort, filters, search, page size, or a value `toVariables` reads
 * from outside the hook — returns to page 0 in the same render, before a request goes out.
 *
 * `rows` is empty while no page has loaded for the current variables.
 *
 * URL persistence and analytics are delegated to `useListPersistence` / `useListAnalytics`.
 */
export const useListPagination = <
  TData,
  TVariables extends OperationVariables,
  TRow,
  TFilters extends object
>({
  query,
  toVariables,
  selectPage,
  skip,
  fetchPolicy,
  initialFilters,
  initialSort = null,
  initialPageSize = DEFAULT_PAGE_SIZE,
  searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
  persist = false,
  analyticsName,
}: UseListPaginationOptions<TData, TVariables, TRow, TFilters>): UseListPaginationResult<
  TRow,
  TFilters
> => {
  const { seed, write: persistToUrl } = useListPersistence<TFilters>(
    persist,
    initialFilters,
    initialSort
  );
  const { trackSort, trackFilter } = useListAnalytics(analyticsName);

  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sort, setSort] = useState<SortState>(seed.sort ?? initialSort);
  const [searchInput, setSearchInput] = useState(seed.search ?? '');
  const [search, setDebouncedSearch] = useState(seed.search ?? '');
  const [filters, setFiltersState] = useState<TFilters>({ ...initialFilters, ...seed.filters });

  // Debounced here rather than with `useDebounce`, so `reset` can clear both values at once.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), searchDebounceMs);
    return () => clearTimeout(timer);
  }, [searchInput, searchDebounceMs]);

  // Mirror search/sort/filters to the URL (no-op unless `persist` is set).
  useEffect(() => {
    persistToUrl({ search, sort, filters });
  }, [persistToUrl, search, sort, filters]);

  const firstPageVariablesKey = JSON.stringify(
    toVariables({ page: 0, pageSize, cursor: null, sort, search, filters })
  );
  const [cursorHistory, setCursorHistory] = useState<CursorHistory>(() =>
    firstPageHistory(firstPageVariablesKey)
  );
  // A changed query starts from its first page. Derived in this render, so the request below
  // never pairs the new variables with an old cursor; the state catches up on the re-render.
  const currentHistory =
    cursorHistory.firstPageVariablesKey === firstPageVariablesKey
      ? cursorHistory
      : firstPageHistory(firstPageVariablesKey);
  if (currentHistory !== cursorHistory) setCursorHistory(currentHistory);
  const { page, cursorForPage } = currentHistory;

  const params = useMemo<ListQueryParams<TFilters>>(
    () => ({ page, pageSize, cursor: cursorForPage[page] ?? null, sort, search, filters }),
    [page, cursorForPage, pageSize, sort, search, filters]
  );

  const shouldSkip = skip ? skip(params) : false;

  // Apollo owns the fetch lifecycle: loading/error, request cancellation, and latest-wins on
  // variable change. Variables are value-compared by Apollo, so a fresh object is fine.
  const {
    data,
    loading,
    error,
    refetch: apolloRefetch,
  } = useQuery<TData, TVariables>(query, {
    variables: toVariables(params),
    skip: shouldSkip,
    fetchPolicy,
  });

  const result = !shouldSkip && data ? selectPage(data) : undefined;

  // Advance only from a loaded page — a second click while the next one loads is ignored —
  // recording the cursor its response returned for the next one.
  const nextPage = useCallback(() => {
    if (loading || !result?.hasMore) return;
    setCursorHistory({
      firstPageVariablesKey,
      page: page + 1,
      cursorForPage: [...cursorForPage.slice(0, page + 1), result.cursor],
    });
  }, [loading, result, firstPageVariablesKey, page, cursorForPage]);

  const prevPage = useCallback(() => {
    if (page === 0) return;
    setCursorHistory({ firstPageVariablesKey, page: page - 1, cursorForPage });
  }, [firstPageVariablesKey, page, cursorForPage]);

  const toggleSort = useCallback(
    (field: string) => {
      const next: SortState =
        sort?.field === field
          ? { field, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
          : { field, direction: 'asc' };
      setSort(next);
      trackSort(next.field, next.direction);
    },
    [sort, trackSort]
  );

  const setFilter = useCallback(
    <K extends keyof TFilters>(key: K, value: TFilters[K]) => {
      setFiltersState(prev => ({ ...prev, [key]: value }));
      trackFilter(key, value);
    },
    [trackFilter]
  );

  const setFilters = useCallback(
    (patch: Partial<TFilters>) => {
      setFiltersState(prev => ({ ...prev, ...patch }));
      Object.entries(patch).forEach(([key, value]) => trackFilter(key, value));
    },
    [trackFilter]
  );

  const refetch = useCallback(() => {
    void apolloRefetch();
  }, [apolloRefetch]);

  // Back to the initial state and the first page. When that is already the current query nothing
  // would be fetched, so it asks again: a reset from the error state is never a no-op.
  const reset = useCallback(() => {
    setPageSize(initialPageSize);
    setSort(initialSort);
    setSearchInput('');
    setDebouncedSearch('');
    setFiltersState(initialFilters);
    setCursorHistory(previous => firstPageHistory(previous.firstPageVariablesKey));
    const initialFirstPageVariablesKey = JSON.stringify(
      toVariables({
        page: 0,
        pageSize: initialPageSize,
        cursor: null,
        sort: initialSort,
        search: '',
        filters: initialFilters,
      })
    );
    if (!shouldSkip && page === 0 && initialFirstPageVariablesKey === firstPageVariablesKey)
      void apolloRefetch();
  }, [
    initialPageSize,
    initialSort,
    initialFilters,
    toVariables,
    shouldSkip,
    page,
    firstPageVariablesKey,
    apolloRefetch,
  ]);

  const pageInfo = useMemo(
    () => derivePageInfo(result, page, pageSize, loading),
    [result, page, pageSize, loading]
  );

  return {
    rows: result?.items ?? [],
    count: result?.count ?? null,
    limitCountExceeded: Boolean(result?.limitCountExceeded),
    loading,
    error: error ?? null,
    pageInfo,
    page,
    pageSize,
    sort,
    search,
    searchInput,
    filters,
    params,
    nextPage,
    prevPage,
    setPageSize,
    toggleSort,
    setSearch: setSearchInput,
    setFilter,
    setFilters,
    refetch,
    reset,
  };
};
